import type {
	ListOverrideAudience,
	ListOverrideEditorViewDto,
	ListOverrideSummaryDto,
	ResolvedListConfigDto,
	SaveListOverrideDto,
	SaveTabStripDto,
	TabStripEditorViewDto,
	TabStripSummaryDto,
} from '@supply-mover-2-0/shared';
import type {
	LayerRef,
	ListProvenance,
	TabProvenance,
} from '../list-builder/overrides/types';
import type { ListConfiguration } from '../list-builder/types';
import { ApiService } from './ApiService';

interface Result<T> {
	data: T;
	success: boolean;
	error?: string;
}

/** `ResolvedListConfigDto` with the `unknown` slots given their core types. */
export interface ResolvedListConfigModel
	extends Omit<ResolvedListConfigDto, 'lists' | 'provenance' | 'tabStrip'> {
	lists: Array<ListConfiguration>;
	provenance: Record<string, ListProvenance>;
	tabStrip: Omit<ResolvedListConfigDto['tabStrip'], 'provenance' | 'layers'> & {
		provenance: Record<string, TabProvenance>;
		layers: Array<LayerRef>;
	};
}

export interface ListOverrideEditorViewModel
	extends Omit<ListOverrideEditorViewDto, 'list' | 'base' | 'provenance'> {
	list: ListConfiguration;
	base: ListConfiguration;
	provenance: ListProvenance;
}

/**
 * Per-listKey cache of the resolved configuration for the current user
 * (PD-14278). Every list mount on a page shares one request, and the List
 * Configuration modal invalidates the key after saving an override so the
 * next mount re-resolves.
 */
const resolvedCache = new Map<
	string,
	Promise<Result<ResolvedListConfigModel | null>>
>();

function fail<T>(data: T, error: string, cause: unknown): Result<T> {
	console.error(error, cause);
	return { data, success: false, error };
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class ListOverridesQueryService {
	// -------------------------------------------------------------- resolved

	/**
	 * The bundled lists of a listKey with the tenant's overrides applied for
	 * the current user. Cached per listKey until {@link invalidateResolved}.
	 * `success: false` means the caller should fall back to the bundled
	 * configuration (the storefront's portal users cannot call this).
	 */
	public static resolved(
		listKey: string,
	): Promise<Result<ResolvedListConfigModel | null>> {
		let pending = resolvedCache.get(listKey);
		if (pending == null) {
			pending = ListOverridesQueryService._fetchResolved(listKey);
			resolvedCache.set(listKey, pending);
			void pending.then((result) => {
				if (!result.success) {
					resolvedCache.delete(listKey);
				}
			});
		}
		return pending;
	}

	/** Resolve as another user — the preview (Workd staff only). */
	public static async resolvedAs(
		listKey: string,
		userId: number,
	): Promise<Result<ResolvedListConfigModel | null>> {
		return ListOverridesQueryService._fetchResolved(listKey, userId);
	}

	public static invalidateResolved(listKey?: string): void {
		if (listKey == null) {
			resolvedCache.clear();
		} else {
			resolvedCache.delete(listKey);
		}
	}

	private static async _fetchResolved(
		listKey: string,
		asUserId?: number,
	): Promise<Result<ResolvedListConfigModel | null>> {
		try {
			const query = asUserId == null ? '' : `?asUserId=${String(asUserId)}`;
			const response = await ApiService.apiGet<{
				success: boolean;
				data: ResolvedListConfigModel;
			}>(`/list-config/${encodeURIComponent(listKey)}/resolved${query}`);
			return { data: response.data, success: response.success };
		} catch (error) {
			return fail(null, 'Failed to resolve list configuration', error);
		}
	}

	// ------------------------------------------------------------- overrides

	public static async list(
		listKey: string,
	): Promise<Result<Array<ListOverrideSummaryDto>>> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data: Array<ListOverrideSummaryDto>;
			}>(`/list-overrides/${encodeURIComponent(listKey)}`);
			return { data: response.data, success: true };
		} catch (error) {
			return fail([], 'Failed to load list overrides', error);
		}
	}

	public static async editorView(
		listKey: string,
		slot: {
			listId: string | null;
			audience: ListOverrideAudience;
			roleId: number | null;
			viaListId?: string;
		},
	): Promise<Result<ListOverrideEditorViewModel | null>> {
		try {
			const params = new URLSearchParams();
			params.set('audience', slot.audience);
			if (slot.listId != null) {
				params.set('listId', slot.listId);
			}
			if (slot.roleId != null) {
				params.set('roleId', String(slot.roleId));
			}
			if (slot.viaListId != null) {
				params.set('viaListId', slot.viaListId);
			}
			const response = await ApiService.apiGet<{
				success: boolean;
				data: ListOverrideEditorViewModel;
			}>(
				`/list-overrides/${encodeURIComponent(listKey)}/editor?${params.toString()}`,
			);
			return { data: response.data, success: true };
		} catch (error) {
			return fail(null, 'Failed to load the override editor', error);
		}
	}

	public static async save(
		dto: SaveListOverrideDto,
	): Promise<Result<ListOverrideSummaryDto | null>> {
		try {
			const response = await ApiService.apiPut<{
				success: boolean;
				data: ListOverrideSummaryDto;
			}>('/list-overrides', dto);
			ListOverridesQueryService.invalidateResolved(dto.listKey);
			return { data: response.data, success: response.success };
		} catch (error) {
			return fail(null, 'Failed to save the override', error);
		}
	}

	public static async remove(
		id: number,
		listKey: string,
	): Promise<Result<null>> {
		try {
			await ApiService.apiDelete(`/list-overrides/${String(id)}`);
			ListOverridesQueryService.invalidateResolved(listKey);
			return { data: null, success: true };
		} catch (error) {
			return fail(null, 'Failed to remove the override', error);
		}
	}

	// ------------------------------------------------------------ tab strips

	public static async tabStrips(
		listKey: string,
	): Promise<Result<Array<TabStripSummaryDto>>> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data: Array<TabStripSummaryDto>;
			}>(`/list-tab-strips/${encodeURIComponent(listKey)}`);
			return { data: response.data, success: true };
		} catch (error) {
			return fail([], 'Failed to load tab strips', error);
		}
	}

	public static async tabStripEditorView(
		listKey: string,
		audience: ListOverrideAudience,
		roleId: number | null,
	): Promise<Result<TabStripEditorViewDto | null>> {
		try {
			const params = new URLSearchParams();
			params.set('audience', audience);
			if (roleId != null) {
				params.set('roleId', String(roleId));
			}
			const response = await ApiService.apiGet<{
				success: boolean;
				data: TabStripEditorViewDto;
			}>(
				`/list-tab-strips/${encodeURIComponent(listKey)}/editor?${params.toString()}`,
			);
			return { data: response.data, success: true };
		} catch (error) {
			return fail(null, 'Failed to load the tab strip editor', error);
		}
	}

	public static async saveTabStrip(
		dto: SaveTabStripDto,
	): Promise<Result<TabStripSummaryDto | null>> {
		try {
			const response = await ApiService.apiPut<{
				success: boolean;
				data: TabStripSummaryDto;
			}>('/list-tab-strips', dto);
			ListOverridesQueryService.invalidateResolved(dto.listKey);
			return { data: response.data, success: response.success };
		} catch (error) {
			return fail(null, 'Failed to save the tab strip', error);
		}
	}

	// ------------------------------------------------------------ role ladder

	public static async roleLadder(): Promise<
		Result<{
			ladder: Array<{ id: number; name: string; precedence: number | null }>;
			roles: Array<{ id: number; name: string }>;
		} | null>
	> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data: {
					ladder: Array<{
						id: number;
						name: string;
						precedence: number | null;
					}>;
					roles: Array<{ id: number; name: string }>;
				};
			}>('/list-overrides-role-ladder');
			return { data: response.data, success: true };
		} catch (error) {
			return fail(null, 'Failed to load the role ladder', error);
		}
	}

	public static async saveRoleLadder(
		roleIds: Array<number>,
	): Promise<Result<null>> {
		try {
			await ApiService.apiPut('/list-overrides-role-ladder', { roleIds });
			ListOverridesQueryService.invalidateResolved();
			return { data: null, success: true };
		} catch (error) {
			return fail(null, 'Failed to save the role ladder', error);
		}
	}
}
